import { createAsyncThunk } from "@reduxjs/toolkit";
import { type FetchActionPayload } from "interfaces";
import { type CampaignOverview } from "../../types/state";
import { type CampaignOverviewRequest } from "../../types/requests";
import { ReducerEntityPrefixTypes, ReducerNamespaceTypes } from "enums";
import { getPrefix } from "features/Application/slices/models";
import { type RootState } from "features/Application/globaltypes/redux";
import { type Filters } from "utils/queryUtils";
import { formatFilters } from "utils/filterMapUtils";
import { campaignFilterTypes } from "../../types/models";
import DataService from "features/Application/services/dataServices/typedDataService";
import { DEFAULT_LOAD_COUNT } from "../../types/constants";
import { escapeTerm } from "utils/searchUtils";
import { campaignAppliedFilterSelector, campaignSearchSelector } from "../selectors";
import { type Columns, ColumnToParamMap } from "../../Overview/getColumnOptions";

interface CampaignsRequest {
  appliedFilter: Filters;
  skip: number;
  top: number;
  term: string;
  sortBy?: string;
  sortOrder?: string;
}

const countHeaderName = process.env.REACT_APP_COUNT_HEADER_NAME as string;

const sendGetCampaignsRequest = (payload: CampaignsRequest, signal?: AbortSignal) => {
  const { appliedFilter, skip, top, term, sortBy, sortOrder } = payload;
  const sortByMapped = ColumnToParamMap[sortBy as Lowercase<Columns>];
  const filterQueryParams = formatFilters(appliedFilter, campaignFilterTypes);

  return DataService.content.getContent(
    {
      top,
      skip,
      sortBy: sortByMapped,
      sortOrder,
      filters: filterQueryParams,
      term,
      showPurchased: true,
      contentType: "campaign",
    },
    signal,
  );
};

export const fetchOverviewCampaigns = createAsyncThunk<FetchActionPayload<CampaignOverview>, CampaignOverviewRequest>(
  getPrefix({
    namespace: ReducerNamespaceTypes.Library,
    entity: ReducerEntityPrefixTypes.ThreatDefenceCampaigns,
    name: "overview",
  }),
  async (request: CampaignOverviewRequest, { getState, signal }) => {
    const rootState = getState() as RootState;
    const appliedFilter = campaignAppliedFilterSelector(rootState);
    const searchTerm = escapeTerm(campaignSearchSelector(rootState));

    const result = await sendGetCampaignsRequest(
      {
        appliedFilter,
        skip: request.skip ?? 0,
        top: request.top ?? DEFAULT_LOAD_COUNT,
        term: searchTerm ?? "",
        sortBy: request.sortBy,
        sortOrder: request.sortDirection,
      },
      signal,
    );

    const data = result.data.map((item) => ({ ...item, type: item.bag?.campaignTypeName }));

    const recordsCount = Number.parseInt(result.headers[countHeaderName]);

    return {
      items: data,
      totalCount: recordsCount,
    };
  },
);
